import * as React from 'react'

import {
  ILocationClosureInterval,
  IWorkflowLocationRestriction,
  RestrictionType,
} from '~/client/graph'
import {
  IControlFields,
  useResourceControls,
} from '~/client/src/shared/hooks/useResourceControls'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Closure from '~/client/src/shared/models/Closure'
import Company from '~/client/src/shared/models/Company'
import type {
  IProposedResourceControls,
  IResourceControlCascade,
  IResourceControlCommands,
  IWorkflowRestrictionDraft,
} from '~/client/src/shared/resources/domain/resourceControls'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { toast } from '~/client/src/shared/utils/toast'

import {
  DATE_CLOSURES_KEY,
  HOURS_OF_OPERATION_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
  normalizeAllToEmpty,
  PERMITTED_COMPANIES_KEY,
} from './controlKeys'

// Locally-added (not-yet-persisted) closures/restrictions need a stable key so the
// editor cells can edit/remove them before Save. Save sends the full desired set,
// so the prefix only matters for keying within the draft — the server replaces.
const DRAFT_ID_PREFIX = '__draft__'

class ResourceControlSkippedError extends Error {}

export interface ResourceControlsDraftApi {
  controls: readonly string[]
  isBookable: boolean
  /** The WR state on the server, which `isBookable` only drafts until Save. */
  savedIsBookable: boolean
  /**
   * Turns WR on right away, outside the draft. For a control that saves on its own ahead of
   * the card's Save (Workflow Types): enabling denies the resource on restricted fields, so it
   * has to land before that control's write, not after it on Save.
   */
  enableNow: () => Promise<void>
  /** Independent display flag (equipment) — not gated by isBookable. */
  showInMap: boolean
  permittedCompanyIds: string[]
  operatingIntervals: IMsDateInterval[]
  closures: Closure[]
  getRestrictions: (type: RestrictionType) => IWorkflowLocationRestriction[]

  setIsBookable: (value: boolean) => void
  setShowInMap: (value: boolean) => void
  setPermittedCompanyIds: (ids: string[]) => void
  setOperatingIntervals: (intervals: IMsDateInterval[]) => void
  saveClosure: (interval: ILocationClosureInterval, closureId?: string) => void
  removeClosure: (closureId: string) => void
  upsertRestrictions: (drafts: IWorkflowRestrictionDraft[]) => void
  deleteRestrictions: (ids: string[]) => void

  isSaving: boolean
  /**
   * Commit the draft. Returns true when the controls were saved (or there was
   * nothing to save), false when the admin cancelled the cascade-confirmation.
   * Does NOT close the panel or clear the dirty flags — the card's single footer
   * Save owns closing and calls markSaved() only after the whole save succeeds.
   */
  save: () => Promise<boolean>
  /**
   * Clear the dirty flags. The card footer calls this only after the FULL save
   * (controls + resource fields) succeeds, so a resource-field save that fails
   * after the controls commit leaves the draft dirty — the retry then re-commits
   * the controls (idempotent) alongside the fields instead of silently leaving
   * the two out of sync.
   */
  markSaved: () => void
  getResourceSaveOverrides: () => Record<string, unknown>
  cancel: () => void
  /** Whether the draft has un-saved edits (drives the footer Save). */
  isDirty: boolean

  // Cascade-confirmation dialog state, rendered by the provider.
  cascade: IResourceControlCascade | null
  isCommittingCascade: boolean
  onConfirmCascade: () => void
  onCancelCascade: () => void
}

interface IParams {
  recordId: string
  projectId: string
  controls: readonly string[]
  controlCommands: IResourceControlCommands
  controlFields: IControlFields
  closuresStore: ClosuresStore
  companies: Company[]
  onClose: () => void
}

function flattenRestrictions(
  fields: IControlFields,
): IWorkflowLocationRestriction[] {
  const groups = fields.workflowRestrictions
  return [
    ...((groups?.maxBookingDurations ?? []) as IWorkflowLocationRestriction[]),
    ...((groups?.maxOverlappingBookings ??
      []) as IWorkflowLocationRestriction[]),
  ]
}

/**
 * Draft + Save orchestration for the resource-card Controls tab. Edits accumulate
 * locally; Save commits them in one batch (Cancel discards). When the resource is
 * bookable, `updateResourceControls` commits all edited control slices in one
 * transaction. Turning it off uses the atomic disable-and-clear command.
 * Reuses the table's cascade-confirmation flow (ResourceControlsContent).
 */
export function useResourceControlsDraft({
  recordId,
  projectId,
  controls,
  controlCommands,
  controlFields,
  closuresStore,
  companies,
  onClose,
}: IParams): ResourceControlsDraftApi {
  const {
    saveBookable,
    saveControls,
    saveShowInMap,
    disableAndClearControls,
    previewControlCascade,
  } = useResourceControls(projectId, controlCommands)

  // Seed every draft slice from the resource's saved controls + the closures
  // store, re-seeding when the selected record (or its saved values) changes.
  const [isBookable, setIsBookable] = React.useState(controlFields.isBookable)
  const [showInMap, setShowInMap] = React.useState(controlFields.showInMap)
  const [permittedCompanyIds, setPermittedCompanyIds] = React.useState<
    string[]
  >(controlFields.permittedCompanies)
  const [operatingIntervals, setOperatingIntervals] = React.useState<
    IMsDateInterval[]
  >(controlFields.operatingIntervals)
  const [restrictions, setRestrictions] = React.useState<
    IWorkflowLocationRestriction[]
  >(() => flattenRestrictions(controlFields))

  // Active closures only — mirrors the Date closures column (getLocationActiveClosures).
  const [closures, setClosures] = React.useState<Closure[]>(() =>
    closuresStore.getLocationActiveClosures(recordId),
  )

  const tempIdRef = React.useRef(0)
  // Per-control dirty flags: the mutation replace-sets each slice, so only send a
  // slice the admin actually edited. Sending an untouched slice would churn its
  // rows and — because the draft is a snapshot that does NOT re-seed same-record
  // background changes — write our stale values back over any update another admin
  // or a subscription made while the card was open. Closures would additionally
  // drop expired non-permit closures, which the active-only seed omits.
  const isBookableDirtyRef = React.useRef(false)
  const showInMapDirtyRef = React.useRef(false)
  const permittedCompaniesDirtyRef = React.useRef(false)
  const operatingIntervalsDirtyRef = React.useRef(false)
  const restrictionsDirtyRef = React.useRef(false)
  const closuresDirtyRef = React.useRef(false)
  const [isSaving, setIsSaving] = React.useState(false)

  const resetDirtyRefs = React.useCallback(() => {
    isBookableDirtyRef.current = false
    showInMapDirtyRef.current = false
    permittedCompaniesDirtyRef.current = false
    operatingIntervalsDirtyRef.current = false
    restrictionsDirtyRef.current = false
    closuresDirtyRef.current = false
  }, [])

  // Re-seed only when a *different* record is selected. Keying on recordId alone —
  // not on controlFields slices, which extractControlFields rebuilds with a fresh
  // array identity on every parent render — keeps an unrelated re-render from wiping
  // the admin's in-progress edits. A background change to the same record's saved
  // values intentionally does NOT re-seed: the draft is a snapshot until Save/Cancel.
  React.useEffect(() => {
    setIsBookable(controlFields.isBookable)
    setShowInMap(controlFields.showInMap)
    setPermittedCompanyIds(controlFields.permittedCompanies)
    setOperatingIntervals(controlFields.operatingIntervals)
    setRestrictions(flattenRestrictions(controlFields))
    setClosures(closuresStore.getLocationActiveClosures(recordId))
    resetDirtyRefs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId])

  // The record whose enableNow has landed while its saved state has not caught up yet, so the
  // Workflow Types control stops treating it as off in between - it would otherwise reopen
  // with every field ticked and a second Apply would allow them all. Keyed by record, so an
  // enable that lands after the card moved on cannot mark another record.
  const [enabledAheadId, setEnabledAheadId] = React.useState<string | null>(
    null,
  )

  React.useEffect(() => {
    setEnabledAheadId(null)
  }, [recordId, controlFields.isBookable])

  const enableNow = React.useCallback(async () => {
    const enabledId = recordId
    await saveBookable([enabledId], true)
    setEnabledAheadId(enabledId)
  }, [saveBookable, recordId])

  // WR toggle marks the draft dirty (its own ref so an untouched Save is a no-op).
  // setIsBookable re-renders, so `isDirty` (computed from the refs) refreshes.
  const handleSetIsBookable = React.useCallback((value: boolean) => {
    isBookableDirtyRef.current = true
    setIsBookable(value)
  }, [])

  // "Show in Map" is independent of the WR toggle and saved via its own mutation,
  // so it gets its own dirty ref; Save persists it separately from the cascade.
  const handleSetShowInMap = React.useCallback((value: boolean) => {
    showInMapDirtyRef.current = true
    setShowInMap(value)
  }, [])

  const nextTempId = () => `${DRAFT_ID_PREFIX}${(tempIdRef.current += 1)}`

  // Wrap the plain state setters so an edit marks the slice dirty; Save then sends
  // only the controls the admin actually touched (see the dirty-flag note above).
  const handleSetPermittedCompanyIds = React.useCallback((ids: string[]) => {
    permittedCompaniesDirtyRef.current = true
    setPermittedCompanyIds(ids)
  }, [])

  const handleSetOperatingIntervals = React.useCallback(
    (intervals: IMsDateInterval[]) => {
      operatingIntervalsDirtyRef.current = true
      setOperatingIntervals(intervals)
    },
    [],
  )

  const handleSaveClosure = React.useCallback(
    (interval: ILocationClosureInterval, closureId?: string) => {
      closuresDirtyRef.current = true
      setClosures(current => {
        if (closureId) {
          return current.map(closure =>
            closure.id === closureId
              ? Closure.fromDto({
                  ...closure.toDto(),
                  closureInterval: interval,
                })
              : closure,
          )
        }
        return [
          ...current,
          Closure.fromDto({
            id: nextTempId(),
            projectId,
            locationId: recordId,
            closureInterval: interval,
          }),
        ]
      })
    },
    [projectId, recordId],
  )

  const handleRemoveClosure = React.useCallback((closureId: string) => {
    closuresDirtyRef.current = true
    setClosures(current => current.filter(closure => closure.id !== closureId))
  }, [])

  const getExistingClosureIds = React.useCallback(
    () =>
      closuresStore
        .getLocationClosures(recordId)
        .filter(closure => !closure.associatedPermitId)
        .map(closure => closure.id),
    [closuresStore, recordId],
  )

  const getClosureIntervalsForSave = React.useCallback(
    () =>
      [
        ...closures.filter(closure => !closure.associatedPermitId),
        ...closuresStore
          .getLocationInactiveClosures(recordId)
          .filter(closure => !closure.associatedPermitId),
      ].map(({ closureInterval }) => ({
        startDate: closureInterval.startDate,
        endDate: closureInterval.endDate,
        isDaily: closureInterval.isDaily,
      })),
    [closures, closuresStore, recordId],
  )

  const upsertRestrictions = React.useCallback(
    (drafts: IWorkflowRestrictionDraft[]) => {
      restrictionsDirtyRef.current = true
      setRestrictions(current => {
        let next = current
        for (const draft of drafts) {
          const restriction: IWorkflowLocationRestriction = {
            id: draft.id ?? nextTempId(),
            projectId,
            locationId: recordId,
            restrictionType: draft.restrictionType,
            values: draft.values,
            workflowIds: draft.workflowIds,
          }
          next = draft.id
            ? next.map(existing =>
                existing.id === draft.id ? restriction : existing,
              )
            : [...next, restriction]
        }
        return next
      })
    },
    [projectId, recordId],
  )

  const deleteRestrictions = React.useCallback((ids: string[]) => {
    restrictionsDirtyRef.current = true
    const removed = new Set(ids)
    setRestrictions(current =>
      current.filter(restriction => !removed.has(restriction.id as string)),
    )
  }, [])

  const getRestrictions = React.useCallback(
    (type: RestrictionType) =>
      restrictions.filter(restriction => restriction.restrictionType === type),
    [restrictions],
  )

  // ---- Cascade-confirmation flow (ported from ResourceControlsContent) ----
  const [cascadeDialog, setCascadeDialog] = React.useState<{
    cascade: IResourceControlCascade
    commit: () => Promise<void>
    resolve: (committed: boolean) => void
  } | null>(null)
  const [isCommittingCascade, setIsCommittingCascade] = React.useState(false)

  const requireRecordUpdated = React.useCallback(
    (
      result: IResourceControlCascade | null,
      message: (count: number) => string,
    ): void => {
      const failedIds = result?.failedLocationIds ?? []
      if (!failedIds.includes(recordId)) return
      toast.error(message(failedIds.length))
      throw new ResourceControlSkippedError()
    },
    [recordId],
  )

  const runWithCascadeConfirmation = React.useCallback(
    async (
      proposed: IProposedResourceControls,
      commit: () => Promise<void>,
    ): Promise<boolean> => {
      let cascade: IResourceControlCascade | null = null
      try {
        cascade = await previewControlCascade([recordId], proposed)
      } catch {
        toast.error(Localization.translator.couldNotCheckAffectedWorkflows)
        return false
      }

      // affectedPermitTypeCount is the authoritative count — affectedPermitTypes is
      // nullable and capped; confirm only when it's non-zero.
      if (!cascade || cascade.affectedPermitTypeCount === 0) {
        await commit()
        return true
      }
      // Hold the Save in-flight (isSaving stays true) until the admin confirms or
      // cancels the blast-radius dialog. Resolves true when committed, false when
      // cancelled — the caller (card Save) aborts the whole save on cancel.
      const confirmed = cascade
      return await new Promise<boolean>(resolve =>
        setCascadeDialog({ cascade: confirmed, commit, resolve }),
      )
    },
    [previewControlCascade, recordId],
  )

  // `showInMap` is intentionally excluded from cascade-aware controls because it
  // saves through its own mutation.
  const cascadeControlsDirty =
    isBookableDirtyRef.current ||
    permittedCompaniesDirtyRef.current ||
    operatingIntervalsDirtyRef.current ||
    restrictionsDirtyRef.current ||
    closuresDirtyRef.current

  const isDirty = cascadeControlsDirty || showInMapDirtyRef.current

  const save = React.useCallback(async (): Promise<boolean> => {
    // Nothing edited — the card Save has nothing to commit for controls.
    if (!isDirty) return true
    setIsSaving(true)
    try {
      // "Show in Map" is an independent display flag saved via its own mutation.
      // When it's the ONLY edit, persist it directly — no cascade preview needed.
      if (!cascadeControlsDirty) {
        await saveShowInMap([recordId], showInMap)
        return true
      }

      // Each branch only picks the proposed controls + the commit; the
      // cascade-confirmation, dirty-reset, and return are shared below.
      let proposed: IProposedResourceControls
      let commit: () => Promise<void>

      if (!isBookable) {
        proposed = { isBookable: false }
        commit = async () => {
          const closureIds = getExistingClosureIds()
          const result = await disableAndClearControls([recordId])
          requireRecordUpdated(
            result,
            Localization.translator.someResourcesCouldNotBeDisabled,
          )
          closuresStore.receiveDeleted(closureIds)
        }
      } else {
        proposed = {
          isBookable: isBookableDirtyRef.current ? true : undefined,
        }
        commit = async () => {
          const replacesRestrictions =
            restrictionsDirtyRef.current &&
            (controls.includes(MAX_BOOKING_DURATION_KEY) ||
              controls.includes(MAX_OVERLAPPING_KEY))
          const replacesClosures =
            closuresDirtyRef.current && controls.includes(DATE_CLOSURES_KEY)
          const closureIds = replacesClosures ? getExistingClosureIds() : []

          // One atomic call sets every edited slice. Omitted slices remain
          // unchanged, while restrictions and closures use replace semantics.
          const result = await saveControls([recordId], {
            isBookable:
              isBookableDirtyRef.current || replacesRestrictions
                ? true
                : undefined,
            permittedCompanies:
              permittedCompaniesDirtyRef.current &&
              controls.includes(PERMITTED_COMPANIES_KEY)
                ? normalizeAllToEmpty(permittedCompanyIds, companies)
                : undefined,
            operatingIntervals:
              operatingIntervalsDirtyRef.current &&
              controls.includes(HOURS_OF_OPERATION_KEY)
                ? operatingIntervals
                : undefined,
            workflowRestrictions: replacesRestrictions
              ? restrictions.map(restriction => ({
                  restrictionType: restriction.restrictionType,
                  values: (restriction.values ?? []).flatMap(value =>
                    typeof value === 'string' ? [value] : [],
                  ),
                  workflowIds: (restriction.workflowIds ?? []).flatMap(id =>
                    typeof id === 'string' ? [id] : [],
                  ),
                }))
              : undefined,
            closures: replacesClosures
              ? getClosureIntervalsForSave()
              : undefined,
          })
          requireRecordUpdated(
            result,
            Localization.translator.someResourcesCouldNotBeUpdated,
          )
          closuresStore.receiveDeleted(closureIds)
        }
      }

      const committed = await runWithCascadeConfirmation(proposed, commit)
      // Cascade cancelled/aborted — persist NOTHING (including "Show in Map"), so
      // Cancel leaves no partial commit.
      if (!committed) return false

      // Cascade accepted. Only now persist the independent "Show in Map" flag, so
      // it never lands ahead of a cascade the admin ends up cancelling.
      if (showInMapDirtyRef.current) {
        await saveShowInMap([recordId], showInMap)
      }
      // Do NOT clear the dirty flags here — the card footer calls markSaved()
      // only after the resource-field save also succeeds. Leaving them set means
      // a field-save failure keeps the draft dirty, so the retry re-commits
      // controls + fields together instead of leaving them out of sync.
      return true
    } catch (error) {
      if (!(error instanceof ResourceControlSkippedError)) {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
      return false
    } finally {
      setIsSaving(false)
    }
  }, [
    isDirty,
    cascadeControlsDirty,
    isBookable,
    showInMap,
    permittedCompanyIds,
    operatingIntervals,
    restrictions,
    controls,
    companies,
    saveControls,
    saveShowInMap,
    disableAndClearControls,
    runWithCascadeConfirmation,
    requireRecordUpdated,
    closuresStore,
    getExistingClosureIds,
    getClosureIntervalsForSave,
    recordId,
  ])

  const onConfirmCascade = React.useCallback(async () => {
    if (!cascadeDialog) return
    setIsCommittingCascade(true)
    let committed = false
    try {
      await cascadeDialog.commit()
      committed = true
    } catch (error) {
      if (!(error instanceof ResourceControlSkippedError)) {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      }
    } finally {
      setIsCommittingCascade(false)
      cascadeDialog.resolve(committed)
      setCascadeDialog(null)
    }
  }, [cascadeDialog])

  const onCancelCascade = React.useCallback(() => {
    if (isCommittingCascade) return
    // Resolve the held Save promise as "not committed" so the card Save aborts;
    // the draft stays for the admin to adjust.
    cascadeDialog?.resolve(false)
    setCascadeDialog(null)
  }, [cascadeDialog, isCommittingCascade])

  const getResourceSaveOverrides = React.useCallback(() => {
    const overrides: Record<string, unknown> = {}
    const clearsControls = isBookableDirtyRef.current && !isBookable
    if (clearsControls || permittedCompaniesDirtyRef.current) {
      overrides.permittedCompanies = clearsControls
        ? []
        : normalizeAllToEmpty(permittedCompanyIds, companies)
    }
    if (clearsControls || operatingIntervalsDirtyRef.current) {
      overrides.operatingIntervals = clearsControls ? [] : operatingIntervals
    }
    return overrides
  }, [companies, isBookable, operatingIntervals, permittedCompanyIds])

  return {
    controls,
    isBookable,
    savedIsBookable: controlFields.isBookable || enabledAheadId === recordId,
    enableNow,
    showInMap,
    permittedCompanyIds,
    operatingIntervals,
    closures,
    getRestrictions,
    setIsBookable: handleSetIsBookable,
    setShowInMap: handleSetShowInMap,
    setPermittedCompanyIds: handleSetPermittedCompanyIds,
    setOperatingIntervals: handleSetOperatingIntervals,
    saveClosure: handleSaveClosure,
    removeClosure: handleRemoveClosure,
    upsertRestrictions,
    deleteRestrictions,
    isSaving,
    isDirty,
    save,
    markSaved: resetDirtyRefs,
    getResourceSaveOverrides,
    cancel: onClose,
    cascade: cascadeDialog?.cascade ?? null,
    isCommittingCascade,
    onConfirmCascade,
    onCancelCascade,
  }
}
